import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/context/ToastContext";
import { ToggleSwitch } from "@/components/base/FormControls";

interface PaymentMethod {
  id: string;
  name: string;
  code: string | null;
  requires_reference: boolean | null;
  active: boolean | null;
}

export default function PaymentMethodsTab() {
  const toast = useToast();
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("payment_methods")
        .select("id, name, code, requires_reference, active")
        .order("created_at", { ascending: true });
      if (error) {
        toast.error("No se pudieron cargar los métodos de pago.");
        return;
      }
      setMethods((data ?? []) as PaymentMethod[]);
    } catch {
      toast.error("No se pudieron cargar los métodos de pago.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (method: PaymentMethod, changes: Partial<PaymentMethod>) => {
    setSavingId(method.id);
    const { error } = await supabase
      .from("payment_methods")
      .update(changes)
      .eq("id", method.id);
    setSavingId(null);
    if (error) {
      toast.error("No se pudo actualizar el método de pago.");
      return;
    }
    setMethods((prev) => prev.map((m) => (m.id === method.id ? { ...m, ...changes } : m)));
    toast.success("Método de pago actualizado.");
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error("Escribe el nombre del método de pago.");
      return;
    }
    setCreating(true);
    try {
      const code = newName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
      const { data, error } = await supabase
        .from("payment_methods")
        .insert({ name: newName.trim(), code, active: true, requires_reference: false })
        .select("id, name, code, requires_reference, active")
        .single();
      if (error || !data) {
        toast.error("No se pudo crear el método de pago.");
        return;
      }
      setMethods((prev) => [...prev, data as PaymentMethod]);
      setNewName("");
      toast.success("Método de pago creado.");
    } catch {
      toast.error("Ocurrió un error inesperado.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-800">
            <i className="ri-add-circle-line text-base"></i>
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Nuevo método de pago</h3>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            placeholder="Ej: Cheque, Crédito, Puntos..."
            className="flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {creating ? (
              <i className="ri-loader-4-line animate-spin text-base"></i>
            ) : (
              <i className="ri-add-line text-base"></i>
            )}
            Agregar
          </button>
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-5">
        <h3 className="font-heading text-sm font-semibold text-foreground-900">
          Métodos de pago configurables
        </h3>
        {loading ? (
          <div className="flex items-center justify-center gap-3 py-10 text-sm text-foreground-500">
            <i className="ri-loader-4-line animate-spin text-lg"></i>
            Cargando...
          </div>
        ) : methods.length === 0 ? (
          <p className="py-6 text-center text-sm text-foreground-500">Sin métodos de pago.</p>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {methods.map((method) => (
              <li
                key={method.id}
                className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 lg:flex-row lg:items-center lg:justify-between"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-200 text-foreground-600">
                    <i className="ri-wallet-3-line text-base"></i>
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground-950">{method.name}</p>
                    <p className="truncate text-[11px] text-foreground-400">
                      Código: {method.code ?? "—"}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <ToggleSwitch
                    label="Requiere referencia"
                    checked={Boolean(method.requires_reference)}
                    onChange={(checked) => patch(method, { requires_reference: checked })}
                  />
                  <button
                    type="button"
                    disabled={savingId === method.id}
                    onClick={() => patch(method, { active: !method.active })}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60 ${
                      method.active
                        ? "border-accent-300 bg-accent-50 text-accent-900"
                        : "border-background-300 bg-background-100 text-foreground-500"
                    }`}
                  >
                    <i className={`${method.active ? "ri-checkbox-circle-line" : "ri-close-circle-line"} text-sm`}></i>
                    {method.active ? "Activo" : "Inactivo"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}